/**
 * AdminSidebarHeader - Reusable header component for admin sidebar sections
 * Displays section icon, title, and optional "+ New" button
 * When collapsed, shows navigation arrows to switch between items
 */
export default function AdminSidebarHeader({
  icon,
  title,
  showAddButton = false,
  onAddClick,
  addButtonTooltip = 'Add new',
  isCollapsed = false,
  // Collapsed mode navigation props
  collapsedTitle = '',
  onPrevious = null,
  onNext = null
}) {
  // Collapsed mode - Show navigation arrows with current item title
  if (isCollapsed) {
    return (
      <div className="border-b py-3 max-lg:py-2 px-2 max-lg:px-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-default)' }}>
        <div className="flex items-center justify-between gap-1">
          {/* Previous Button */}
          <button
            onClick={onPrevious}
            disabled={!onPrevious}
            className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${
              onPrevious
                ? 'text-purple-400 hover:text-purple-300'
                : 'cursor-not-allowed'
            }`}
            style={!onPrevious ? { color: 'var(--color-text-disabled)' } : {}}
            onMouseEnter={(e) => {
              if (onPrevious) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (onPrevious) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            title={onPrevious ? 'Previous' : 'No previous item'}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Current Item Title */}
          <div className="flex-1 min-w-0 text-center px-1">
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--color-text-primary)' }} title={collapsedTitle}>
              {collapsedTitle}
            </p>
          </div>

          {/* Next Button */}
          <button
            onClick={onNext}
            disabled={!onNext}
            className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${
              onNext
                ? 'text-purple-400 hover:text-purple-300'
                : 'cursor-not-allowed'
            }`}
            style={!onNext ? { color: 'var(--color-text-disabled)' } : {}}
            onMouseEnter={(e) => {
              if (onNext) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (onNext) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            title={onNext ? 'Next' : 'No next item'}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Expanded mode - Normal header
  return (
    <div className="border-b px-4 max-lg:px-3 py-3 max-lg:py-2 flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-default)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 max-lg:space-x-1.5">
          <svg className="w-5 h-5 max-lg:w-4 max-lg:h-4 text-purple-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
          <h3 className="text-lg max-lg:text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>{title}</h3>
        </div>

        {showAddButton && (
          <button
            onClick={onAddClick}
            className="p-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 transition-all duration-200"
            style={{ color: 'var(--color-text-on-accent)' }}
            title={addButtonTooltip}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
